import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

// Initialize Sentry
export const initSentry = () => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: !import.meta.env.DEV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    // Set sample rate for profiling - this is relative to tracesSampleRate
    profilesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Set user context
    beforeSend(event, hint) {
      // Filter out development errors in production
      if (import.meta.env.MODE === 'production' && event.exception) {
        const error = hint.originalException;
        if (error instanceof Error) {
          // Filter out common development errors
          if (
            error.message.includes('ResizeObserver loop limit exceeded') ||
            error.message.includes('Non-Error promise rejection captured') ||
            error.message.includes('ChunkLoadError')
          ) {
            return null;
          }
        }
      }
      return event;
    },
    enableLogs: ['staging', 'production'].includes(import.meta.env.MODE),
  });
};

// Set user context
export const setSentryUser = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
};

// Clear user context
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// Set additional context
export const setSentryContext = (
  key: string,
  context: Record<string, symbol>
) => {
  Sentry.setContext(key, context);
};

// Capture exception
export const captureException = (
  error: Error,

  context?: Record<string, any>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.keys(context).forEach((key) => {
        scope.setContext(key, context[key]);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

// Capture message
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

// Add breadcrumb
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};
